// Map Search Scripts
(function() {
    let overlayArray = [];
    let pinMarkerArray = [];
    let openInfoWindow = null;
    let mapCenter = { lat: 38.522458, lng: -83.704500 };
    let defaultZoom = 6;

    function roundPrice10k(value) {
        if (!value) return null;
        return Math.round(value / 10000) * 10;
    }

    // fitBounds() alone can zoom in very tight when the bounds cover only a
    // handful of pins close together (e.g. one market with 2-3 nearby
    // communities). This corrects that one-time auto-fit result without
    // capping the map's zoom generally — the user can still zoom in past
    // MAX_AUTO_FIT_ZOOM themselves afterward.
    const MAX_AUTO_FIT_ZOOM = 14;
    function fitBoundsCapped(gmap, bounds, padding) {
        // fitBounds() sets the resulting zoom synchronously (confirmed:
        // getZoom() reflects it immediately, no need to wait on an event),
        // so just correct it right after rather than fight event timing.
        if (padding !== undefined) {
            gmap.fitBounds(bounds, padding);
        } else {
            gmap.fitBounds(bounds);
        }
        if (gmap.getZoom() > MAX_AUTO_FIT_ZOOM) gmap.setZoom(MAX_AUTO_FIT_ZOOM);
    }

    function buildInfoWindowContent(locObj, onClose) {
        const priceMin = roundPrice10k(locObj.min_price);
        const priceMax = roundPrice10k(locObj.max_price);
        let priceHtml = '';
        if (priceMin) {
            priceHtml = `<p class="fw-bold mb-0">From $${priceMin}k${priceMax ? ` - $${priceMax}k+` : ''}</p>`;
        }
        let cityState = [locObj.city, locObj.state].filter(Boolean).join(', ');

        const wrapper = document.createElement('div');
        wrapper.className = 'oi-infowindow no-min';
        wrapper.style.width = '250px';

        let imageHtml = '';
        if (locObj.thumbnail) {
            imageHtml = `<div class="location-pin-iw-img">
                <img src="${locObj.thumbnail}" alt="${locObj.name}">
                <button class="location-pin-iw-close" type="button" aria-label="Close">&times;</button>
            </div>`;
        }

        wrapper.innerHTML = `<a href="${locObj.permalink}" class="text-decoration-none">
            ${imageHtml}
            <div class="p-2">
                <h6 class="fw-bold mb-0">${locObj.name}</h6>
                ${cityState ? `<p class="small mb-1">${cityState}</p>` : ''}
                ${priceHtml}
            </div>
        </a>`;

        if (locObj.thumbnail) {
            wrapper.querySelector('.location-pin-iw-close').addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            });
        }

        return wrapper;
    }

    // Builds the "N Communities / N Move-in Ready" popup content for a market label.
    function buildMarketInfoWindowContent(marketObj, onClose) {
        const wrapper = document.createElement('div');
        wrapper.className = 'market-label-iw-content';
        wrapper.innerHTML = `
            <button class="market-label-iw-close" type="button" aria-label="Close">&times;</button>
            <p class="mb-1"><strong>${marketObj.locations ?? 0}</strong> Communit${(marketObj.locations ?? 0) === 1 ? 'y' : 'ies'}</p>
            <p class="mb-0"><strong>${marketObj.specs ?? 0}</strong> Move-in Ready</p>
        `;

        wrapper.querySelector('.market-label-iw-close').addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        });

        return wrapper;
    }

    // Renders a market label as a real <a> in overlayMouseTarget so native clicks work.
    function createMarketLabelOverlay(marketObj, gmap, onClick) {
        const position = new google.maps.LatLng(
            parseFloat(marketObj.latitude),
            parseFloat(marketObj.longitude)
        );

        const overlay = new google.maps.OverlayView();

        const closeIw = () => {
            infoWindow.close();
            if (openInfoWindow === infoWindow) openInfoWindow = null;
        };

        const infoWindow = new google.maps.InfoWindow({
            content: buildMarketInfoWindowContent(marketObj, closeIw),
            position,
            disableAutoPan: true,
            // The label is centered on `position` via translate(-50%, -50%), so
            // Maps' default tail placement lands partway down into the label
            // instead of at its top. Shift the whole window up so the tail's
            // tip just touches the label's top edge instead of overlapping it.
            pixelOffset: new google.maps.Size(0, -13),
        });

        // Scope this specific infowindow instance for styling (mirrors location-pin-iw).
        google.maps.event.addListener(infoWindow, 'domready', () => {
            const container = document.querySelector('.gm-style-iw-c');
            if (!container) return;
            container.classList.add('market-label-iw');
        });

        overlay.onAdd = function() {
            const el = document.createElement('a');
            el.className = 'market-marker-label';
            el.textContent = marketObj.market_name;
            if (typeof onClick === 'function') {
                el.href = '#';
                el.addEventListener('click', e => {
                    e.preventDefault();
                    onClick(marketObj);
                });
            } else {
                el.href = marketObj.permalink;
            }
            el.addEventListener('mouseenter', () => {
                el.style.zIndex = 9999;
                if (openInfoWindow && openInfoWindow !== infoWindow) openInfoWindow.close();
                infoWindow.open({ map: gmap });
                openInfoWindow = infoWindow;
            });
            el.addEventListener('mouseleave', () => { el.style.zIndex = ''; });
            overlay._el = el;
            this.getPanes().overlayMouseTarget.appendChild(el);
        };

        overlay.draw = function() {
            const point = this.getProjection().fromLatLngToDivPixel(position);
            if (point && overlay._el) {
                overlay._el.style.position = 'absolute';
                overlay._el.style.left = point.x + 'px';
                overlay._el.style.top = point.y + 'px';
                overlay._el.style.transform = 'translate(-50%, -50%)';
            }
        };

        overlay.onRemove = function() {
            infoWindow.close();
            if (overlay._el?.parentNode) {
                overlay._el.parentNode.removeChild(overlay._el);
                overlay._el = null;
            }
        };

        overlay.setMap(gmap);
        return overlay;
    }

    function createLocationPin(locObj, gmap, pinSVG) {
        const closeIw = () => {
            infoWindow.close();
            openInfoWindow = null;
        };

        const infoWindow = new google.maps.InfoWindow({
            content: buildInfoWindowContent(locObj, closeIw),
            disableAutoPan: true,
        });

        // Scope the Maps container and hide the native close button (we supply our own)
        google.maps.event.addListener(infoWindow, 'domready', () => {
            const container = document.querySelector('.gm-style-iw-c');
            if (!container) return;
            container.classList.add('location-pin-iw');
            document.querySelector('.gm-style-iw-d')?.classList.add('location-pin-iw-body');
        });

        const marker = new google.maps.Marker({
            position: {
                lat: parseFloat(locObj.latitude),
                lng: parseFloat(locObj.longitude),
            },
            map: gmap,
            title: locObj.name,
            icon: {
                url: pinSVG,
                scaledSize: new google.maps.Size(27, 37),
                anchor: new google.maps.Point(13, 37),
            },
        });

        marker.addListener('mouseover', () => {
            if (openInfoWindow) openInfoWindow.close();
            infoWindow.open({ map: gmap, anchor: marker });
            openInfoWindow = infoWindow;
        });

        marker.addListener('click', () => {
            if (locObj.permalink) window.location.href = locObj.permalink;
        });

        return marker;
    }

    function clearOverlays() {
        for (const overlay of overlayArray) overlay.setMap(null);
        overlayArray = [];
    }

    function clearPins() {
        for (const marker of pinMarkerArray) marker.setMap(null);
        pinMarkerArray = [];
        if (openInfoWindow) { openInfoWindow.close(); openInfoWindow = null; }
    }

    function setResetBtnActive(active) {
        document.getElementById('btn-reset-map')?.classList.toggle('active', active);
    }

    // Every programmatic pan/zoom animation on a given map carries a "job id" (a plain
    // counter stamped on the gmap instance). Each animation step checks its id is still
    // current before acting -- superseded by a newer zoomToMarket() call, or canceled by
    // the user touching the map (see watchForUserInteraction), and it just stops instead
    // of continuing to fight for control of the zoom/pan. Without this, a user clicking
    // the zoom controls mid-animation had their change immediately overwritten by the next
    // scheduled animation step, making +/- feel unresponsive for as long as it ran.
    function startNewAnimationJob(gmap) {
        gmap.__oiAnimationJobId = (gmap.__oiAnimationJobId || 0) + 1;
        return gmap.__oiAnimationJobId;
    }

    function watchForUserInteraction(gmap) {
        if (gmap.__oiInteractionWatched) return;
        gmap.__oiInteractionWatched = true;
        // Zoom control buttons and map dragging both live inside the map's own container,
        // so a single capture-phase listener there covers both without needing to tell them
        // apart -- any direct interaction cancels whatever animation is in flight.
        gmap.getDiv().addEventListener('pointerdown', () => startNewAnimationJob(gmap), { capture: true });
    }

    // Google Maps has no native smooth fitBounds()/setZoom() -- panTo()'s built-in
    // animation only kicks in for moves smaller than one viewport, so a market-to-market
    // jump would just snap instantly regardless. Interpolate the pan manually so motion is
    // smooth at any distance.
    function smoothPanTo(gmap, targetLatLng, jobId, duration = 600) {
        const start = gmap.getCenter();
        if (!start) {
            gmap.setCenter(targetLatLng);
            return;
        }
        const startLat = start.lat();
        const startLng = start.lng();
        const endLat = targetLatLng.lat();
        const endLng = targetLatLng.lng();
        const startTime = performance.now();

        function step(now) {
            if (gmap.__oiAnimationJobId !== jobId) return;
            const t = Math.min(1, (now - startTime) / duration);
            gmap.setCenter({
                lat: startLat + (endLat - startLat) * t,
                lng: startLng + (endLng - startLng) * t,
            });
            if (t < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
    }

    // There's no native animated zoom in the Maps JS API either -- step one zoom level at
    // a time on a plain timer (not chained off the map's 'idle' event, since smoothPanTo's
    // concurrent setCenter() calls also fire 'idle' and would misfire the sequencing).
    function smoothZoomTo(gmap, targetZoom, jobId, stepDelay = 90) {
        if (gmap.__oiAnimationJobId !== jobId) return;
        const current = gmap.getZoom();
        if (current === targetZoom) return;
        const next = current + (targetZoom > current ? 1 : -1);
        gmap.setZoom(next);
        if (next !== targetZoom) {
            setTimeout(() => smoothZoomTo(gmap, targetZoom, jobId, stepDelay), stepDelay);
        }
    }

    function zoomToMarket(marketObj, gmap, pinSVG) {
        clearOverlays();
        clearPins();
        setResetBtnActive(true);
        watchForUserInteraction(gmap);
        const jobId = startNewAnimationJob(gmap);

        const fromCenter = gmap.getCenter();
        const fromZoom = gmap.getZoom();
        let toCenter;
        let toZoom;

        if (marketObj.location_pins?.length) {
            const bounds = new google.maps.LatLngBounds();
            for (const locObj of marketObj.location_pins) {
                pinMarkerArray.push(createLocationPin(locObj, gmap, pinSVG));
                bounds.extend(new google.maps.LatLng(parseFloat(locObj.latitude), parseFloat(locObj.longitude)));
            }
            // fitBounds() sets the resulting zoom/center synchronously (confirmed:
            // getZoom()/getCenter() reflect it immediately), so compute the target this
            // way, then revert before the browser ever paints the intermediate state.
            fitBoundsCapped(gmap, bounds);
            toZoom = gmap.getZoom();
            toCenter = gmap.getCenter();
        } else {
            toCenter = new google.maps.LatLng(parseFloat(marketObj.latitude), parseFloat(marketObj.longitude));
            toZoom = 10;
            gmap.setCenter(toCenter);
            gmap.setZoom(toZoom);
        }

        if (fromCenter) {
            gmap.setCenter(fromCenter);
            gmap.setZoom(fromZoom);
        }

        smoothPanTo(gmap, toCenter, jobId);
        smoothZoomTo(gmap, toZoom, jobId);
    }

    // Bridges the search-map filters (oifilter.js's initSearchMapFilters) to the same zoom
    // used for a pin/label click. `window._oiSearchMapInstances` is populated per map
    // instance in search_map.html's inline script (keyed by mapId) as each map goes idle.
    // `locationPins` always comes from the filter AJAX response (scoped to the full current
    // filter combination, not just market). When nothing matches, `fallbackCenter` (the
    // actual County/City picked, resolved server-side) is used instead of the market's own
    // office coordinates -- falls back to the static marketMapJSON blob's market lat/lng
    // only when no more specific center is available (e.g. no City/County was selected).
    // The newest selection is held here rather than on the map instances themselves. The
    // fullscreen modal's map is not built until the user first opens that modal (see
    // buildMap() in search_map.html), so a market picked before that has no instance to
    // be stored on. `pendingZoomApplied` records which maps already consumed the current
    // selection, so re-opening the modal leaves the view the user left behind alone.
    let pendingZoom = null;
    const pendingZoomApplied = new Set();

    // A map inside a closed modal is display:none, and fitBounds()/setZoom() on a
    // zero-size container is unreliable, so only a visible map takes its zoom.
    function isInstanceVisible(instance) {
        const container = document.getElementById(instance.mapId);
        return !!(container && container.offsetWidth > 0 && container.offsetHeight > 0);
    }

    function applyPendingZoom(instance) {
        if (!pendingZoom || !instance || pendingZoomApplied.has(instance.mapId)) return;
        if (!isInstanceVisible(instance)) return;

        const marketObj = (instance.marketMapJSON || []).find(
            (m) => String(m.market_id) === String(pendingZoom.marketId)
        );
        if (!marketObj) return;

        const pins = pendingZoom.pins;
        const effectiveMarketObj = { ...marketObj, location_pins: pins };
        if (pins.length === 0 && pendingZoom.fallbackCenter) {
            effectiveMarketObj.latitude = pendingZoom.fallbackCenter.latitude;
            effectiveMarketObj.longitude = pendingZoom.fallbackCenter.longitude;
        }

        pendingZoomApplied.add(instance.mapId);
        zoomToMarket(effectiveMarketObj, instance.searchMap.map, instance.locationPinSVG);
    }

    function applyPendingZoomToAll() {
        Object.values(window._oiSearchMapInstances || {}).forEach(applyPendingZoom);
    }

    window.zoomAllSearchMapsToMarket = function(marketId, locationPins, fallbackCenter) {
        pendingZoom = {
            marketId: marketId,
            pins: locationPins || [],
            fallbackCenter: fallbackCenter || null,
        };
        pendingZoomApplied.clear();
        applyPendingZoomToAll();
    };

    // A map that was hidden when the market was picked takes its zoom once it is visible:
    // on the modal opening when the map already exists, or on the map's first idle when
    // this open is what built it.
    document.addEventListener('DOMContentLoaded', () => {
        document.getElementById('search-map-modal')?.addEventListener('shown.bs.modal', applyPendingZoomToAll);
    });

    window.addEventListener('oimap_idle', (event) => {
        // search_map.html registers the instance in its own oimap_idle listener. Wait a
        // tick so this runs after that one, whichever order the two were added in.
        setTimeout(() => {
            applyPendingZoom((window._oiSearchMapInstances || {})[event.detail?.map_id]);
        }, 0);
    });

    // Clicking a market pin/label should populate the dependent filters the same way
    // picking it from the <select> does. Sets the select's value and dispatches a
    // native "change" so oifilter.js's initMarketCascade (the single source of truth
    // for "market changed") runs its existing AJAX population -- works whether or not
    // #filters-offcanvas is currently open, since setting/reading a <select> and its
    // change listeners are unaffected by the offcanvas's visibility:hidden state.
    function syncMarketSelect(marketId) {
        const marketSelect = document.querySelector('#filters-offcanvas select[name="market"]');
        if (!marketSelect || String(marketSelect.value) === String(marketId)) return;
        marketSelect.value = String(marketId);
        marketSelect.dispatchEvent(new Event('change'));
    }

    function addAllMarkers(searchMap, marketMapJSON, pinSVG, builderPermalink, mode, locationPinSVG) {
        clearOverlays();
        clearPins();
        setResetBtnActive(false);

        const onClick = mode === 'zoom'
            ? (marketObj) => {
                zoomToMarket(marketObj, searchMap.map, locationPinSVG);
                syncMarketSelect(marketObj.market_id);
            }
            : null;

        for (const marketObj of marketMapJSON) {
            overlayArray.push(createMarketLabelOverlay(marketObj, searchMap.map, onClick));
        }
    }

    function applyMapOffset(searchMap) {
        if (window.innerWidth >= 768) {
            searchMap.map.panBy(-Math.round(window.innerWidth * 0.25), 0);
        }
    }

    // A fixed zoom/center covers a much smaller geographic area on a narrow
    // (mobile) container than on a wide desktop one, so market labels near
    // the edges can fall outside the viewport. Fit the map to the actual
    // markets instead, with padding for the label overlays' own width/height.
    function fitAllMarkets(searchMap, marketMapJSON) {
        if (!marketMapJSON || !marketMapJSON.length) {
            searchMap.map.setCenter(mapCenter);
            searchMap.map.setZoom(defaultZoom);
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        for (const marketObj of marketMapJSON) {
            bounds.extend(new google.maps.LatLng(parseFloat(marketObj.latitude), parseFloat(marketObj.longitude)));
        }
        fitBoundsCapped(searchMap.map, bounds, 60);
    }

    window.initStatePolygons = function(event, searchMap, stateJSON, marketMapJSON, pinSVG, builderPermalink, mode, locationPinSVG) {
        fitAllMarkets(searchMap, marketMapJSON);
        addAllMarkers(searchMap, marketMapJSON, pinSVG, builderPermalink, mode, locationPinSVG);
        if (mode !== 'zoom') applyMapOffset(searchMap);
    };

    window.resetMap = function(event, searchMap, stateJSON, marketMapJSON, pinSVG, builderPermalink, mode, locationPinSVG) {
        fitAllMarkets(searchMap, marketMapJSON);
        addAllMarkers(searchMap, marketMapJSON, pinSVG, builderPermalink, mode, locationPinSVG);
    };

    window.anchorHomeMap = function() {
        let myModalEl = document.getElementById('search-modal');
        let myNav = document.getElementById('main-nav');
        let scrollToDiv = document.getElementById('map-search');

        let searchModal = bootstrap.Modal.getInstance(myModalEl);
        let mobileNav = bootstrap.Offcanvas.getInstance(myNav);

        if (searchModal != null) {
            searchModal.hide();
        }
        if (mobileNav != null) {
            mobileNav.hide();
        }
        if (scrollToDiv != null) {
            scrollToDiv.scrollIntoView({ behavior: 'smooth' });
        }
    };
})();
